<template>
  <b-card header-tag="header" footer-tag="footer">
    <template v-slot:header>
      <h6 class="mb-0" v-if="showName">Device Configuration ({{ getSafe(() => device.object) }})</h6>
      <h6 class="mb-0" v-else>Device Configuration</h6>
    </template>
   
    <template v-if="loading"> 
      <div class="d-flex justify-content-center mb-3">
        <b-spinner variant="primary"></b-spinner>
      </div>
    </template>

    <div v-else>
      <!-- OPMODE Configuration -->
      <b-form-group
        label="Operation Mode:"
        label-for="opmode-select"
        description="Controls how the relay behaves"
      >
        <b-form-select
          id="opmode-select"
          v-model="selectedOpMode"
          :options="opModeOptions"
          :disabled="sendingOpMode"
        ></b-form-select>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="primary"
            @click="sendOpMode"
            :disabled="sendingOpMode"
          >
            <b-spinner small v-show="sendingOpMode"></b-spinner>
            Apply
          </b-button>
        </div>
      </b-form-group>

      <hr class="my-3">

      <!-- STATUSIND Configuration -->
      <b-form-group
        label="Status Indicator:"
        label-for="statusind-select"
        description="Controls the behavior of the status LED"
      >
        <b-form-select
          id="statusind-select"
          v-model="selectedStatusInd"
          :options="statusIndOptions"
          :disabled="sendingStatusInd"
        ></b-form-select>
        <div class="d-flex justify-content-end mt-2">
          <b-button
            variant="primary"
            @click="sendStatusInd"
            :disabled="sendingStatusInd"
          >
            <b-spinner small v-show="sendingStatusInd"></b-spinner>
            Apply
          </b-button>
        </div>
      </b-form-group>

      <!-- Current Configuration Status -->
      <b-alert show variant="info" class="mt-3">
        <strong>Current Configuration:</strong>
        <div>Operation Mode: {{ getOpModeLabel(selectedOpMode) }}</div>
        <div>Status Indicator: {{ getStatusIndLabel(selectedStatusInd) }}</div>
      </b-alert>
    </div>
  </b-card>
</template>

<script>
import { BROADCAST_ACTION } from "@/core/services/store/devices.module";
import Pusher from 'pusher-js';

export default {
  name: "suitch-configuration",
  components: {},
  props: {
    device: {
      type: Object,
      required: true
    },
    showName: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      loading: true,
      selectedOpMode: 0,
      selectedStatusInd: 0,
      sendingOpMode: false,
      sendingStatusInd: false,
      
      // Options for OPMODE
      opModeOptions: [
        { value: 0, text: 'Mode 0: Normal operation' },
        { value: 1, text: 'Mode 1: Inverted operation' },
        { value: 2, text: 'Mode 2: Toggle operation' },
        { value: 3, text: 'Mode 3: Pulse operation' }
      ],
      
      // Options for STATUSIND
      statusIndOptions: [
        { value: 0, text: 'Mode 0: LED follows relay state' },
        { value: 1, text: 'Mode 1: LED always on' },
        { value: 2, text: 'Mode 2: LED blinks on state change' }
      ]
    };
  },
  mounted() {
    this.initializeComponent();
  },
  created() {
    this.setupRealTimeUpdates();
  },
  methods: {
    getSafe(fn) {
      try { 
        return fn(); 
      } catch (e) {
        // Silently handle errors
        return '';
      }
    },
    
    initializeComponent() {
      // Simulate fetching current configuration from the device
      // In a real implementation, you would query the device for its current settings
      setTimeout(() => {
        this.loading = false;
        // Default values - in a real implementation, these would come from the device
        this.selectedOpMode = 0;
        this.selectedStatusInd = 0;
      }, 1000);
    },
    
    setupRealTimeUpdates() {
      let pusher = new Pusher('a41aed5f65a8c69c5f0a', { cluster: 'us2' });
      pusher.subscribe('device-updates');
      pusher.bind('configuration-updated', data => {
        console.log(">>> Real-time configuration update:", data);
        // Update local state if the update is for this device
        if (data.token === this.device.token) {
          if (data.type === 'OPMODE') {
            this.selectedOpMode = parseInt(data.value);
          } else if (data.type === 'STATUSIND') {
            this.selectedStatusInd = parseInt(data.value);
          }
        }
      });
    },
    
    sendOpMode() {
      this.sendCommand('OPMODE', this.selectedOpMode);
    },
    
    sendStatusInd() {
      this.sendCommand('STATUSIND', this.selectedStatusInd);
    },
    
    sendCommand(commandType, value) {
      // Set the appropriate loading state
      if (commandType === 'OPMODE') {
        this.sendingOpMode = true;
      } else if (commandType === 'STATUSIND') {
        this.sendingStatusInd = true;
      }
      
      const commandData = `${commandType}:${value}`;
      
      this.$store
        .dispatch(BROADCAST_ACTION, { 
          token: this.device.token,
          secret: this.device.secret_token,
          command_data: commandData 
        })
        .then((response) => {
          // Handle successful response
          console.log(`${commandType} command sent successfully:`, response);
          
          // Show success notification
          this.$bvToast.toast(`${commandType} setting updated successfully`, {
            title: 'Success',
            variant: 'success',
            solid: true
          });
        })
        .catch((error) => {
          // Handle error
          console.error(`Error sending ${commandType} command:`, error);
          
          // Show error notification
          this.$bvToast.toast(`Failed to update ${commandType} setting`, {
            title: 'Error',
            variant: 'danger',
            solid: true
          });
        })
        .finally(() => {
          // Reset loading state
          if (commandType === 'OPMODE') {
            this.sendingOpMode = false;
          } else if (commandType === 'STATUSIND') {
            this.sendingStatusInd = false;
          }
        });
    },
    
    getOpModeLabel(value) {
      const option = this.opModeOptions.find(opt => opt.value === value);
      return option ? option.text : 'Unknown';
    },
    
    getStatusIndLabel(value) {
      const option = this.statusIndOptions.find(opt => opt.value === value);
      return option ? option.text : 'Unknown';
    }
  },
  watch: {
    device: {
      handler() {
        this.initializeComponent();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.card-header {
  background-color: #f8f9fa;
}

.form-group {
  margin-bottom: 1.5rem;
}
</style>